import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import QuizBreakdown, { BreakdownQuestion } from '../components/QuizBreakdown';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

interface UserStats {
  totalQuizzes: number;
  averageScore: number;
  bestScore: number;
  currentStreak: number;
  averagePercentCorrect?: number;
  totalCorrect?: number;
  totalQuestionsAttempted?: number;
}
interface XpData {
  totalXP: number;
  currentLevel: number;
  xpToNextLevel: number;
  progressToNextLevel: number;
}
interface QuizResult {
  resultId: string;
  dateCompleted: string;
  score: number;
  questionsCorrect: number;
  totalQuestions: number;
  topicSlot?: number | null;
  topicName?: string | null;
}
interface UserBadge {
  id: string;
  badge?: { name: string };
  earnedAt?: string;
}
interface TrendPoint {
  date: string;
  score: number;
  questionsCorrect: number;
  completionTime: number;
}
interface CategoryStat {
  category: string;
  attempts: number;
  averageScore: number;
  percentageCorrect: number;
  color: string;
}
interface TopicStat {
  topicSlot: number;
  topicName: string;
  topicColor?: string;
  attempts: number;
  percentageCorrect: number;
}

const TOPIC_ORDER = [1, 2, 3, 4, 5];
const TOPIC_NAMES: Record<number, string> = {
  1: 'Pop Culture',
  2: 'Science & Nature',
  3: 'History & Politics',
  4: 'Geography & World',
  5: 'Sports & Games',
};
/** Topic colors for By Topic chart – matches Compete section topic cards (TopicIllustration gradient + hue-rotate) */
const TOPIC_COLORS: Record<number, string> = {
  1: '#C2828C', // Pop Culture – dusty rose
  2: '#72966A', // Science & Nature – olive moss green
  3: '#7C9BBE', // History & Politics – slate blue
  4: '#B1AB75', // Geography & World – khaki green
  5: '#BA9484', // Sports & Games – terracotta
};

const ACCURACY_CORRECT_COLOR = '#4a7c59';
const ACCURACY_INCORRECT_COLOR = '#a85d5d';
const CHART_LINE_COLOR = '#70798c';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [xpData, setXpData] = useState<XpData | null>(null);
  const [quizHistory, setQuizHistory] = useState<QuizResult[]>([]);
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([]);
  const [topicStats, setTopicStats] = useState<TopicStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [breakdowns, setBreakdowns] = useState<Record<string, BreakdownQuestion[]>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState<Record<string, boolean>>({});
  const [retryCount, setRetryCount] = useState(0);

  useDocumentTitle('Cognidaily | Profile');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      const isPremium = user?.isPremium ?? false;
      const requestConfig = { timeout: 15000 };

      // Phase 1: Fetch profile first (essential) - show profile as soon as this succeeds
      const profilePromise = axios.get('/api/user/profile', requestConfig);
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), 16000)
      );
      let profileResult: PromiseSettledResult<any>;
      try {
        const result = await Promise.race([profilePromise, timeoutPromise]);
        profileResult = { status: 'fulfilled' as const, value: result };
      } catch (err) {
        profileResult = { status: 'rejected' as const, reason: err };
      }

      const profile = profileResult.status === 'fulfilled' ? profileResult.value : null;
      const profileFailed = profileResult.status === 'rejected' || profile?.data?.success === false || (profile?.status && profile.status >= 400);

      if (profileFailed) {
        const err = profileResult.status === 'rejected' ? profileResult.reason : null;
        const msg =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')
            ? 'Request timed out. Please try again.'
            : 'Failed to load profile. Please try again.');
        if (!cancelled) {
          setError(msg);
          setLoading(false);
        }
        return;
      }

      if (profile?.data?.success && profile?.data?.data?.xp) setXpData(profile.data.data.xp);
      if (!cancelled) setLoading(false);

      // Phase 2: Fetch stats and badges in background (non-blocking)
      const statsPromise = axios.get('/api/user/statistics', { params: { quizType: 'main', days: 30 }, ...requestConfig });
      const badgesPromise = axios.get('/api/badges/my', requestConfig);
      const resultsPromise = isPremium
        ? axios.get('/api/quiz/results', { params: { limit: 20, quizType: 'main', days: 30 }, ...requestConfig })
        : Promise.resolve({ data: { success: true, data: { results: [] } } });

      Promise.allSettled([statsPromise, resultsPromise, badgesPromise]).then(([statsRes, resultsRes, badgesRes]) => {
        if (cancelled) return;
        const stats = statsRes.status === 'fulfilled' ? statsRes.value : null;
        const results = resultsRes.status === 'fulfilled' ? resultsRes.value : null;
        const badges = badgesRes.status === 'fulfilled' ? badgesRes.value : null;
        if (stats?.data?.success && stats?.data?.data) {
          const d = stats.data.data;
          if (d.overall) setStats(d.overall);
          if (d.recentTrend && Array.isArray(d.recentTrend)) setTrendData([...d.recentTrend].reverse());
          if (d.byCategory && Array.isArray(d.byCategory)) setCategoryStats(d.byCategory.slice(0, 8));
          if (d.byTopic && Array.isArray(d.byTopic)) setTopicStats(d.byTopic);
        }
        if (results?.data?.success && results?.data?.data?.results)
          setQuizHistory(results.data.data.results);
        if (badges?.data?.success && badges?.data?.data?.badges)
          setBadges(badges.data.data.badges);
      });
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  const fetchBreakdown = useCallback(async (resultId: string) => {
    if (breakdowns[resultId]) return;
    setLoadingBreakdown((prev) => ({ ...prev, [resultId]: true }));
    try {
      const res = await axios.get(`/api/quiz/results/${resultId}/breakdown`);
      if (res.data.success && res.data.data?.questions) {
        setBreakdowns((prev) => ({ ...prev, [resultId]: res.data.data.questions }));
      }
    } catch {
      // 403 if not premium
    } finally {
      setLoadingBreakdown((prev) => ({ ...prev, [resultId]: false }));
    }
  }, [breakdowns]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (d: string) => {
    try {
      return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return d;
    }
  };

  const formatChartDate = (d: string) => {
    try {
      return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return d;
    }
  };

  if (loading)
    return (
      <section className="section">
        <p>Loading...</p>
      </section>
    );
  if (error)
    return (
      <section className="section">
        <p role="alert" className="text-error">
          {error}
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setRetryCount((c) => c + 1)}>
          Try again
        </button>
        <p><Link to="/" className="link">Home</Link></p>
      </section>
    );

  const xp = xpData || {
    totalXP: 0,
    currentLevel: 1,
    xpToNextLevel: 0,
    progressToNextLevel: 0,
  };
  const s = stats || {
    totalQuizzes: 0,
    averageScore: 0,
    bestScore: 0,
    currentStreak: 0,
    averagePercentCorrect: 0,
    totalCorrect: 0,
    totalQuestionsAttempted: 0,
  };
  const isPremium = user?.isPremium ?? false;
  const accuracy = s.averagePercentCorrect ?? 0;
  const accuracyData = [
    { name: 'Correct', value: accuracy, color: ACCURACY_CORRECT_COLOR },
    { name: 'Incorrect', value: 100 - accuracy, color: ACCURACY_INCORRECT_COLOR },
  ];

  const xpForNextLevel = xp.xpToNextLevel || 0;
  const xpProgress = xp.progressToNextLevel ?? 0;

  return (
    <main className="profile-page">
      <header className="profile-page__header">
        <div className="profile-page__header-main">
          <h1 className="profile-page__title">
            {user?.username}
            {isPremium ? ' ★' : ''}
          </h1>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </header>

      <section className="profile-page__overview card">
        <h2 className="profile-page__section-title">Overview</h2>
        <div className="profile-page__hero">
          <p className="profile-page__level">Level {xp.currentLevel}</p>
          <p className="profile-page__xp-total">{xp.totalXP.toLocaleString()} XP</p>
          <div className="profile-page__xp-bar">
            <div className="profile-page__xp-bar-track">
              <div
                className="profile-page__xp-bar-fill"
                style={{ width: `${Math.min(100, xpProgress * 100)}%` }}
              />
            </div>
            <p className="profile-page__xp-bar-label">
              {xpForNextLevel > 0
                ? `${Math.round(xpProgress * 100)}% to Level ${xp.currentLevel + 1}`
                : 'Max level'}
            </p>
          </div>
        </div>
        <dl className="profile-page__stats-dl">
          <div className="profile-page__stat-item">
            <dt>Quizzes</dt>
            <dd>{s.totalQuizzes}</dd>
          </div>
          <div className="profile-page__stat-item">
            <dt>Best score</dt>
            <dd>{s.bestScore}</dd>
          </div>
          <div className="profile-page__stat-item">
            <dt>Avg score</dt>
            <dd>{s.averageScore}</dd>
          </div>
          <div className="profile-page__stat-item">
            <dt>Streak</dt>
            <dd>{s.currentStreak} days</dd>
          </div>
        </dl>
      </section>

      <div className="profile-page__charts-grid">
        <div className="profile-page__charts">
        <section className="profile-page__chart card">
          <h2 className="profile-page__section-title">Accuracy</h2>
          {s.totalQuizzes > 0 ? (
            <div className="profile-page__donut-wrap">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={accuracyData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {accuracyData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => `${value.toFixed(1)}%`}
                    contentStyle={{
                      backgroundColor: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <p className="profile-page__donut-center">
                {accuracy.toFixed(1)}%
                <span className="profile-page__donut-sublabel">correct</span>
              </p>
            </div>
          ) : (
            <p className="profile-page__empty">Complete quizzes to see accuracy.</p>
          )}
        </section>

        <section className="profile-page__chart card">
          <h2 className="profile-page__section-title">Score over time</h2>
          {trendData.length > 0 ? (
            <div className="profile-page__line-wrap">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={trendData.map((d) => ({
                    ...d,
                    label: formatChartDate(d.date),
                  }))}
                  margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border)"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    formatter={(value: number) => [value, 'Score']}
                    labelFormatter={(label) => label}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={CHART_LINE_COLOR}
                    strokeWidth={2}
                    dot={{ fill: CHART_LINE_COLOR, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="profile-page__empty">Complete quizzes to see trends.</p>
          )}
        </section>
        </div>
      </div>

      {(topicStats.length > 0 || categoryStats.length > 0) && (
        <section className="profile-page__topics card">
          {topicStats.length > 0 && (
            <>
              <h2 className="profile-page__section-title">By topic</h2>
              <div className="profile-page__topic-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={TOPIC_ORDER.map((slot) => {
                      const t = topicStats.find((s) => s.topicSlot === slot);
                      return {
                        name: t?.topicName ?? TOPIC_NAMES[slot] ?? `Topic ${slot}`,
                        percentageCorrect: t?.percentageCorrect ?? 0,
                        attempts: t?.attempts ?? 0,
                        color: t?.topicColor ?? TOPIC_COLORS[slot] ?? CHART_LINE_COLOR,
                      };
                    })}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                      formatter={(value: number) => [`${value}%`, 'Avg % correct']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="percentageCorrect" radius={[2, 2, 0, 0]}>
                      {TOPIC_ORDER.map((_, i) => (
                        <Cell
                          key={i}
                          fill={
                            topicStats.find((s) => s.topicSlot === TOPIC_ORDER[i])?.topicColor ??
                            TOPIC_COLORS[TOPIC_ORDER[i]] ??
                            CHART_LINE_COLOR
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
          {categoryStats.length > 0 && (
            <>
              <h2 className="profile-page__section-title profile-page__section-title--sub">
                By subtopic
              </h2>
              <div className="profile-page__topic-list">
                {categoryStats.map((cat) => (
                  <div key={cat.category} className="profile-page__topic-item">
                    <span className="profile-page__topic-label">{cat.category}</span>
                    <div className="profile-page__topic-bar-track">
                      <span
                        className="profile-page__topic-bar"
                        style={{
                          width: `${cat.percentageCorrect}%`,
                          backgroundColor: cat.color || CHART_LINE_COLOR,
                        }}
                      />
                    </div>
                    <span className="profile-page__topic-value">
                      {cat.percentageCorrect}% ({cat.attempts})
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <section className="profile-page__badges card">
        <h2 className="profile-page__section-title">Badges</h2>
        {badges.length === 0 ? (
          <p className="profile-page__empty">No badges yet.</p>
        ) : (
          <ul className="profile-page__badge-list">
            {badges.map((b) => (
              <li key={b.id}>{b.badge?.name || 'Badge'}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="profile-page__quizzes card">
        <h2 className="profile-page__section-title">Recent quizzes</h2>
        {!isPremium ? (
          <div className="profile-page__quiz-history-cta">
            <p className="profile-page__empty">
              As a paying member of Cognidaily, we&rsquo;ll store your quiz history for you. Upgrade to Premium to view your past quizzes, see which questions you got right and wrong, and track your progress over time.
            </p>
            <Link to="/premium" className="btn btn-primary">
              Upgrade to Premium
            </Link>
          </div>
        ) : quizHistory.length === 0 ? (
          <p className="profile-page__empty">No quizzes yet.</p>
        ) : (
          <div className="profile-quiz-list">
            {quizHistory.slice(0, 20).map((q) =>
              isPremium ? (
                <details
                  key={q.resultId}
                  className="profile-quiz-item"
                  onToggle={(e) => {
                    const details = e.currentTarget;
                    if (details.open) fetchBreakdown(q.resultId);
                  }}
                >
                  <summary className="profile-quiz-summary">
                    <span className="profile-quiz-meta">
                      {formatDate(q.dateCompleted)}
                      {q.topicName && ` · ${q.topicName}`}
                    </span>
                    <span className="profile-quiz-score">
                      {q.questionsCorrect}/{q.totalQuestions} ({q.score} pts)
                    </span>
                  </summary>
                  <div className="profile-quiz-breakdown">
                    {loadingBreakdown[q.resultId] ? (
                      <p className="profile-quiz-loading">Loading breakdown...</p>
                    ) : breakdowns[q.resultId] ? (
                      <QuizBreakdown
                        questions={breakdowns[q.resultId]}
                        title=""
                        className="profile-quiz-breakdown-inner"
                      />
                    ) : null}
                  </div>
                </details>
              ) : (
                <div
                  key={q.resultId}
                  className="profile-quiz-item profile-quiz-item--simple"
                >
                  <span className="profile-quiz-meta">
                    {formatDate(q.dateCompleted)}
                    {q.topicName && ` · ${q.topicName}`}
                  </span>
                  <span className="profile-quiz-score">
                    {q.questionsCorrect}/{q.totalQuestions} ({q.score} pts)
                  </span>
                </div>
              )
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default Profile;
